import { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import ModalDialog from "../../../../shared/ModalDialog";
import Message from "../../../../../i18n/Message";
import label from "../../../../../i18n/label";
import backend from "../../../../../backend/backend";
import Notify from "../../../../shared/Notify";
import Spinner from "../../../../../assets/icons/Spinner";
import PlusIcon from "../../../../../assets/icons/PlusIcon";
import LogoConfluence from "../../../../../assets/icons/LogoConfluence";

import "./SelectIntegrationDialog.scss";

const STEP_SELECT = 1;
const STEP_CONFIGURE = 2;

const SelectIntegrationDialog = ({ kbase, workspaceId, onClose, onSuccess }) => {
  const intl = useIntl();
  const [step, setStep] = useState(STEP_SELECT);
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [pageUrl, setPageUrl] = useState("");
  const [includeNested, setIncludeNested] = useState(true);
  const [autoSync, setAutoSync] = useState(true);

  const [formElem, setFormElem] = useState(null);

  useEffect(() => {
    backend
      .getIntegrations(workspaceId)
      .then((data) => {
        // Filter to only show Confluence connections
        const confluenceConnections = data.filter(
          (conn) => conn.type === "CONFLUENCE"
        );
        setConnections(confluenceConnections);
        setLoadingConnections(false);
      })
      .catch((err) => {
        console.error("Failed to load integration connections", err);
        Notify.error(label(intl, "error_unexpected"));
        setLoadingConnections(false);
      });
  }, [workspaceId, intl]);

  const handleSelectConnection = (connection) => {
    setSelectedConnection(connection);
    setStep(STEP_CONFIGURE);
  };

  const handleBack = () => {
    setStep(STEP_SELECT);
    setSelectedConnection(null);
  };

  const handleFinish = (e) => {
    e.preventDefault();

    const valid = formElem?.checkValidity();
    if (formElem) formElem.reportValidity();
    if (!valid) return;

    setSaving(true);

    const config = {
      integrationConnectionUid: selectedConnection.uid,
      type: "CONFLUENCE",
      pageUrl: pageUrl,
      autoSync: autoSync,
    };

    backend
      .configureKBaseIntegration(workspaceId, kbase.uid, config)
      .then(() => {
        Notify.success(label(intl, "kbase_integration_config_saved"));
        setSaving(false);
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch((err) => {
        console.error("Failed to configure integration", err);
        Notify.error(label(intl, "error_save_integration_config"));
        setSaving(false);
      });
  };

  return (
    <ModalDialog onClose={onClose} className="select_integration_dialog">
      {step === STEP_SELECT && (
        <>
          <div className="modal_title">
            <Message id="kbase_integration_select_connection" />
          </div>
          <div className="dialog_body">
            {loadingConnections && (
              <div className="loading_state">
                <Spinner size={32} />
              </div>
            )}

            {!loadingConnections && connections.length === 0 && (
              <div className="empty_state">
                <p>
                  <Message id="kbase_integration_no_connections" />
                </p>
              </div>
            )}

            {!loadingConnections && connections.length > 0 && (
              <div className="connection_grid">
                {connections.map((connection) => (
                  <div
                    key={connection.uid}
                    className="connection_card"
                    onClick={() => handleSelectConnection(connection)}
                  >
                    <div className="connection_logo">
                      <LogoConfluence size={48} />
                    </div>
                    <div className="connection_name">{connection.name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {step === STEP_CONFIGURE && (
        <>
          <div className="modal_title">
            <Message id="kbase_integration_configure_details" />
          </div>
          <form onSubmit={handleFinish} ref={(val) => setFormElem(val)}>
            <div className="dialog_body">
              <div className="form_group">
                <label>
                  <Message id="kbase_integration_page_url" />
                </label>
                <input
                  className="form-control"
                  type="url"
                  required
                  placeholder={label(
                    intl,
                    "kbase_integration_page_url_placeholder"
                  )}
                  value={pageUrl}
                  onChange={(e) => setPageUrl(e.target.value)}
                  readOnly={saving}
                />
                <div className="form_help">
                  The URL of the Confluence page to sync (including all nested
                  pages if enabled below)
                </div>
              </div>

              <div className="form_group checkbox_group">
                <label>
                  <input
                    type="checkbox"
                    checked={includeNested}
                    onChange={(e) => setIncludeNested(e.target.checked)}
                    disabled={saving}
                  />
                  <Message id="kbase_integration_include_nested" />
                </label>
              </div>

              <div className="form_group checkbox_group">
                <label>
                  <input
                    type="checkbox"
                    checked={autoSync}
                    onChange={(e) => setAutoSync(e.target.checked)}
                    disabled={saving}
                  />
                  <Message id="kbase_integration_auto_sync" />
                </label>
              </div>
            </div>

            <div className="buttons">
              <button
                className="btn no-btn"
                type="button"
                onClick={handleBack}
                disabled={saving}
              >
                <Message id="integration_back" />
              </button>
              <button
                className="btn bg-gradient-dark"
                type="submit"
                disabled={saving}
              >
                {saving ? (
                  <Spinner size="18" />
                ) : (
                  <PlusIcon color="#fff" strokeWidth="2" size="18" />
                )}
                <Message id="integration_finish" />
              </button>
            </div>
          </form>
        </>
      )}
    </ModalDialog>
  );
};

export default SelectIntegrationDialog;

